import { Sheet, SheetHistoryEntry, HistoryActionType, Pouch, Group, SheetSnapshot } from '../types';
import { calculateGroupPlaceholders } from '../logic/GroupLogic';

export class HistoryLogic {
  static addEntry(
    sheet: Sheet, 
    groups: Group[], 
    pouches: Pouch[], 
    action: HistoryActionType, 
    description: string,
    targetId?: string,
    forceNewEntry?: boolean
  ): Sheet {
    // 一時的なアニメーション用プロパティを除外したpouchesとgroupsをコピー
    const cleanPouches = pouches.map(pouch => {
      // 型アサーションを使用して安全にプロパティを削除
      const cleanPouch = { ...pouch };
      const pouchAny = cleanPouch as any;
      
      // 各種UIフラグを削除
      delete pouchAny._animateFromZero;
      delete pouchAny._isDragging;
      delete pouchAny._isMoving;
      delete pouchAny._isDeleting;
      
      // _animateMultiplierと_animateSignChangeはtrueに設定（Undo/Redo時のアニメーション用）
      pouchAny._animateMultiplier = true;
      pouchAny._animateSignChange = true;
      
      return cleanPouch;
    });

    // グループにプレースホルダー名を計算して設定
    let cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      // 各種UIフラグを削除
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    // グループをGROUP_COLORSの順、SUBTOTALの高い順にソートしてプレースホルダー名を計算
    cleanGroups = calculateGroupPlaceholders(cleanGroups, cleanPouches);

    const entry: SheetHistoryEntry = {
      timestamp: new Date(),
      groups: cleanGroups,
      pouches: cleanPouches,
      description,
      type: action,
      targetId,
      balancingPouchId: sheet.balancingPouchId ?? null,
      balancingTargetTotal: sheet.balancingTargetTotal,
      ...(action === HistoryActionType.ADD_POUCH ? { addPouchAwaitingFirstValue: true as const } : {}),
    };
  
    // 履歴がない場合は新しいエントリを追加
    if (!sheet.history || sheet.history.length === 0) {
      return {
        ...sheet,
        history: [entry],
        historyIndex: 0
      };
    }

    // 新規パウチ ADD の直後の初回値確定: EDIT を別エントリにせず、ADD を最終値のスナップショットで上書き
    const latestForAddMerge = sheet.history[sheet.historyIndex];
    if (
      action === HistoryActionType.EDIT_POUCH_VALUE &&
      targetId !== undefined &&
      sheet.historyIndex >= 0 &&
      latestForAddMerge.type === HistoryActionType.ADD_POUCH &&
      latestForAddMerge.targetId === targetId &&
      latestForAddMerge.addPouchAwaitingFirstValue === true
    ) {
      const updatedHistory = [...sheet.history];
      updatedHistory[sheet.historyIndex] = {
        ...latestForAddMerge,
        timestamp: new Date(),
        groups: cleanGroups,
        pouches: cleanPouches,
        addPouchAwaitingFirstValue: false,
      };
      return {
        ...sheet,
        history: updatedHistory,
      };
    }

    // 以下のアクションタイプは連続した同一対象の操作を1つのエントリとして扱う
    const mergeableActions = [
      HistoryActionType.EDIT_POUCH_VALUE,
      HistoryActionType.CHANGE_GROUP_COLOR,
      HistoryActionType.CHANGE_POUCH_COLOR,
      HistoryActionType.PASTE_BULK_UPDATE
    ];
    
    // 最新のエントリ
    const latestEntry = sheet.history[sheet.historyIndex];
    
    // 連続した同一対象への操作かどうかを判定（ADD_POUCH / DELETE_POUCH の連続マージは行わない）
    const shouldMergeEntry = !forceNewEntry && (
      (mergeableActions.includes(action) && // 統合対象のアクションタイプか
      latestEntry.type === action && // 前回と同じアクションタイプか
      latestEntry.targetId === targetId && // 同じ対象への操作か
      targetId !== undefined && // 対象IDが指定されているか
      sheet.historyIndex > 0) || // historyIndexが0より大きい場合のみ統合する（[0]を保護）
      // タイトル編集は常に最新のエントリを上書き（タイトル編集をどのアクションの一部としても適用できるようにする）
      ((action === HistoryActionType.EDIT_POUCH_TITLE || action === HistoryActionType.EDIT_GROUP_TITLE) && 
       sheet.historyIndex > 0)  // historyIndexが0より大きい場合のみ統合する（[0]を保護）
    );
    
    
    if (shouldMergeEntry) {
      // 新しいエントリで上書き（historyIndexは変更しない）
      const updatedHistory = [...sheet.history];
      
      // タイトル編集の場合は、アクションタイプとdescriptionは元のままにしてpouchesとgroupsだけを更新
      if (action === HistoryActionType.EDIT_POUCH_TITLE || action === HistoryActionType.EDIT_GROUP_TITLE) {
        updatedHistory[sheet.historyIndex] = {
          ...updatedHistory[sheet.historyIndex],
          pouches: cleanPouches,
          groups: cleanGroups
        };
      } else {
        // 通常の上書き
        updatedHistory[sheet.historyIndex] = entry;
      }
      
      const result = {
        ...sheet,
        history: updatedHistory
        // historyIndexは変更しない
      };
      
      return result;
    }
    
    // 通常の履歴追加処理
    const startIndex = sheet.historyIndex + 1;
    const newHistory = sheet.history.slice(0, startIndex);
    const updatedHistory = [...newHistory, entry];


    const result = {
      ...sheet,
      history: updatedHistory,
      historyIndex: startIndex
    };
    
    return result;
  }

  /**
   * 直近の履歴が「指定パウチの Add Pouch」のときだけそのエントリを削除する。
   * キーボード新規を Esc で破棄したとき、Add の履歴も残さない用途。
   */
  static stripLatestHistoryEntryIfAddPouchForTarget(sheet: Sheet, pouchId: string): Sheet | null {
    if (!sheet.history?.length || sheet.historyIndex == null || sheet.historyIndex < 0) {
      return null;
    }
    const entry = sheet.history[sheet.historyIndex];
    if (entry.type !== HistoryActionType.ADD_POUCH || entry.targetId !== pouchId) {
      return null;
    }
    if (sheet.historyIndex === 0) {
      return null;
    }
    const newHistory = sheet.history.slice(0, sheet.historyIndex);
    const newIndex = sheet.historyIndex - 1;
    return {
      ...sheet,
      history: newHistory,
      historyIndex: newIndex
    };
  }

  static undo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex <= 0) return null;

    const newIndex = sheet.historyIndex - 1;
    const targetState = sheet.history[newIndex];
    const currentState = sheet.history[sheet.historyIndex];
    
    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );
    let targetPouches = animationResult.pouches;

    // 統一的なアニメーション判定の結果を使用
    let targetGroups = animationResult.groups;
    
    // ADD_GROUPからのundoの場合、addingフラグを設定しない（通常の状態に戻す）
    if (currentState.type !== HistoryActionType.DELETE_GROUP && currentState.type !== HistoryActionType.MERGE_GROUP && targetState.type === HistoryActionType.ADD_GROUP) {
      // currentStateのグループにaddingフラグがついている場合は削除
      targetGroups = targetGroups.map(g => {
        if ((g as any)._isAdding) {
          return { ...g, _isAdding: false } as Group;
        }
        return g;
      });
    }

    const newSheet = {
      ...sheet,
      historyIndex: newIndex,
      pouches: targetPouches,
      groups: targetGroups,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal
    };
    
    return {
      newSheet,
      newPouches: targetPouches,
      newGroups: targetGroups
    };
  }

  static redo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex >= sheet.history.length - 1) return null;

    const newIndex = sheet.historyIndex + 1;
    const targetState = sheet.history[newIndex];
    const currentState = sheet.history[sheet.historyIndex];
    
    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );
    let targetPouches = animationResult.pouches;

    // 統一的なアニメーション判定の結果を使用
    let targetGroups = animationResult.groups;

    return {
      newSheet: {
        ...sheet,
        historyIndex: newIndex,
        balancingPouchId: targetState.balancingPouchId ?? null,
        balancingTargetTotal: targetState.balancingTargetTotal
      },
      newPouches: targetPouches,
      newGroups: targetGroups
    };
  }

  static createSnapshot(
    sheet: Sheet,
    pouches: Pouch[],
    groups: Group[],
    name: string,
    description: string
  ): Sheet {
    // スナップショット作成時も同様に一時的なプロパティを除外
    const cleanPouches = pouches.map(pouch => {
      const cleanPouch = { ...pouch };
      const pouchAny = cleanPouch as any;
      
      delete pouchAny._animateFromZero;
      delete pouchAny._isDragging;
      delete pouchAny._isMoving;
      delete pouchAny._isDeleting;
      
      // _animateMultiplierと_animateSignChangeはtrueに設定（Undo/Redo時のアニメーション用）
      pouchAny._animateMultiplier = true;
      pouchAny._animateSignChange = true;
      
      return cleanPouch;
    });

    const cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      // 各種UIフラグを削除
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    const snapshot: SheetSnapshot = {
      id: `snapshot_${Date.now()}`,
      timestamp: new Date(),
      name,
      description,
      pouches: cleanPouches,
      groups: cleanGroups
    };

    return {
      ...sheet,
      snapshots: [...(sheet.snapshots || []), snapshot]
    };
  }

  static restoreSnapshot(
    sheet: Sheet,
    snapshotId: string
  ): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    const snapshot = sheet.snapshots?.find(s => s.id === snapshotId);
    if (!snapshot) return null;

    // スナップショットの状態を履歴に追加
    const sheetWithHistory = this.addEntry(
      sheet,
      snapshot.groups,
      snapshot.pouches,
      HistoryActionType.RESTORE_SNAPSHOT,
      `Load Snapshot`
    );

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      sheet.pouches,
      snapshot.pouches,
      sheet.groups,
      snapshot.groups
    );

    return {
      newSheet: sheetWithHistory,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  static updateSnapshotName(sheet: Sheet, snapshotId: string, newName: string): Sheet {
    return {
      ...sheet,
      snapshots: sheet.snapshots?.map(snapshot =>
        snapshot.id === snapshotId ? { ...snapshot, name: newName } : snapshot
      ) || []
    };
  }

  static deleteSnapshot(sheet: Sheet, snapshotId: string): Sheet {
    return {
      ...sheet,
      snapshots: sheet.snapshots?.filter(s => s.id !== snapshotId) || []
    };
  }

  static overwriteSnapshot(
    sheet: Sheet,
    pouches: Pouch[],
    groups: Group[],
    snapshotId: string
  ): Sheet {
    // 一時的なプロパティを除外
    const cleanPouches = pouches.map(pouch => {
      const cleanPouch = { ...pouch };
      const pouchAny = cleanPouch as any;
      
      delete pouchAny._animateFromZero;
      delete pouchAny._isDragging;
      delete pouchAny._isMoving;
      delete pouchAny._isDeleting;
      
      pouchAny._animateMultiplier = true;
      pouchAny._animateSignChange = true;
      
      return cleanPouch;
    });

    const cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    return {
      ...sheet,
      snapshots: sheet.snapshots?.map(snapshot =>
        snapshot.id === snapshotId
          ? {
              ...snapshot,
              timestamp: new Date(),
              pouches: cleanPouches,
              groups: cleanGroups
            }
          : snapshot
      ) || []
    };
  }

  /**
   * 現在の履歴エントリを削除してundo（一つ前のエントリに移動）
   * @param sheet 現在のシート
   * @returns 新しいシート状態とアニメーション用のパウチ・グループ配列
   */
  static shiftUndo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex <= 0) return null;

    const currentState = sheet.history[sheet.historyIndex];
    const targetState = sheet.history[sheet.historyIndex - 1];
    
    // 現在の履歴エントリを削除
    const newHistory = sheet.history.filter((_, index) => index !== sheet.historyIndex);
    const newHistoryIndex = sheet.historyIndex - 1;
    
    const newSheet = {
      ...sheet,
      history: newHistory,
      historyIndex: newHistoryIndex,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal
    };

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );

    return {
      newSheet,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  /**
   * 現在の履歴エントリを削除してredo（一つ先のエントリに移動）
   * @param sheet 現在のシート
   * @returns 新しいシート状態とアニメーション用のパウチ・グループ配列
   */
  static shiftRedo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex >= sheet.history.length - 1) return null;

    const currentState = sheet.history[sheet.historyIndex];
    const targetState = sheet.history[sheet.historyIndex + 1];
    
    // 現在の履歴エントリを削除
    const newHistory = sheet.history.filter((_, index) => index !== sheet.historyIndex);
    // 削除後、historyIndexはそのまま（配列がずれるため）
    const newHistoryIndex = sheet.historyIndex;
    
    const newSheet = {
      ...sheet,
      history: newHistory,
      historyIndex: newHistoryIndex,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal
    };

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );

    return {
      newSheet,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  /**
   * 現在の状態と復元後の状態を比較して、アニメーション用フラグを自動設定
   * @param currentPouches 現在のパウチ配列
   * @param targetPouches 復元後のパウチ配列
   * @param currentGroups 現在のグループ配列
   * @param targetGroups 復元後のグループ配列
   * @returns アニメーション用フラグが設定されたパウチとグループ配列
   */
  static applyAnimationFlags(
    currentPouches: Pouch[],
    targetPouches: Pouch[],
    currentGroups: Group[],
    targetGroups: Group[]
  ): { pouches: Pouch[]; groups: Group[] } {
    // パウチのアニメーション判定
    const currentPouchIds = new Set(currentPouches.map(p => p.id));
    const targetPouchIds = new Set(targetPouches.map(p => p.id));
    
    // 削除されるパウチID = 現在にあって復元後にないパウチID
    const deletedPouchIds = Array.from(currentPouchIds).filter(id => !targetPouchIds.has(id));
    // 追加されるパウチID = 復元後にあって現在にないパウチID
    const addedPouchIds = Array.from(targetPouchIds).filter(id => !currentPouchIds.has(id));
    
    // パウチにアニメーションフラグを設定
    const animatedPouches = targetPouches.map(pouch => {
      const currentPouch = currentPouches.find(p => p.id === pouch.id);
      
      if (addedPouchIds.includes(pouch.id)) {
        // 追加されるパウチ：_animateFromZeroフラグ
        return { ...pouch, _animateFromZero: true } as Pouch;
      } else if (currentPouch) {
        // 既存のパウチ：値やマルチの変更をチェック
        const hasValueChange = currentPouch.value !== pouch.value;
        const hasMultiplierChange = currentPouch.multiplier !== pouch.multiplier;
        
        return {
          ...pouch,
          _animateSignChange: hasValueChange,
          _animateMultiplier: hasMultiplierChange
        } as Pouch;
      }
      return pouch;
    });
    
    // 削除されるパウチを一時的に追加（_isDeletingフラグ付き）
    const deletedPouches = currentPouches
      .filter(p => deletedPouchIds.includes(p.id))
      .map(p => ({ ...p, _isDeleting: true } as Pouch));
    
    const finalPouches = [...animatedPouches, ...deletedPouches];
    
    // グループのアニメーション判定
    const currentGroupIds = new Set(currentGroups.map(g => g.id));
    const targetGroupIds = new Set(targetGroups.map(g => g.id));
    
    // 削除されるグループID = 現在にあって復元後にないグループID
    const deletedGroupIds = Array.from(currentGroupIds).filter(id => !targetGroupIds.has(id));
    // 追加されるグループID = 復元後にあって現在にないグループID
    const addedGroupIds = Array.from(targetGroupIds).filter(id => !currentGroupIds.has(id));
    
    // グループにアニメーションフラグを設定
    const animatedGroups = targetGroups.map(group => {
      if (addedGroupIds.includes(group.id)) {
        // 追加されるグループ：_isAddingフラグ
        return { ...group, _isAdding: true, _isDeleting: false } as Group;
      }
      return { ...group, _isAdding: false, _isDeleting: false } as Group;
    });
    
    // 削除されるグループを一時的に追加（_isDeletingフラグ付き）
    const deletedGroups = currentGroups
      .filter(g => deletedGroupIds.includes(g.id))
      .map(g => ({ ...g, _isDeleting: true, _isAdding: false } as Group));
    
    const finalGroups = [...animatedGroups, ...deletedGroups];
    
    return {
      pouches: finalPouches,
      groups: finalGroups
    };
  }
} 